import React, { useEffect, useMemo, useState } from 'react';
import { USER_GUIDE_ROLES, USER_GUIDES } from '../data/userGuidesContent';
import './UserGuidesSection.css';

const chapterSearchHaystack = (chapter) => {
  const sectionText = (chapter.sections || []).flatMap((section) =>
    [section.title, section.path, section.summary, section.imageAlt].filter(Boolean)
  );
  return [chapter.title, chapter.intro, chapter.instruction, chapter.number, ...sectionText]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const chapterMatchesSearch = (chapter, term) => {
  if (!term) return true;
  return chapterSearchHaystack(chapter).includes(term);
};

const moduleListLabel = (chapter) => {
  const title = chapter.title || 'Module';
  return chapter.number ? `${chapter.number}. ${title}` : title;
};

const ManualSection = ({ section }) => (
  <article className="ug-block">
    {section.title ? <h3 className="ug-block-title">{section.title}</h3> : null}
    {section.path ? <p className="ug-path">{section.path}</p> : null}
    {section.image ? (
      <figure className="ug-frame">
        <img
          src={section.image}
          alt={section.imageAlt || section.title || 'Guide screenshot'}
          className="ug-shot"
        />
      </figure>
    ) : null}
    {section.summary ? (
      <div className="ug-note">
        <span className="ug-note-label">Summary</span>
        <p>{section.summary}</p>
      </div>
    ) : null}
  </article>
);

const ManualChapter = ({ chapter }) => (
  <section className="ug-chapter" aria-labelledby="ug-active-module-title">
    <header className="ug-chapter-head">
      <h2 className="ug-title" id="ug-active-module-title">
        {chapter.title}
      </h2>
      {chapter.intro ? <p className="ug-lead">{chapter.intro}</p> : null}
      {chapter.instruction ? (
        <div className="ug-note">
          <span className="ug-note-label">Instruction</span>
          <p>{chapter.instruction}</p>
        </div>
      ) : null}
    </header>
    {(chapter.sections || []).map((section) => (
      <ManualSection key={section.image || section.title || section.number} section={section} />
    ))}
  </section>
);

const UserGuidesSection = ({ searchTerm = '' }) => {
  const [activeRole, setActiveRole] = useState('admin');
  const [activeChapterIndex, setActiveChapterIndex] = useState(0);

  const term = searchTerm.trim().toLowerCase();

  const guide = useMemo(() => USER_GUIDES[activeRole], [activeRole]);
  const chapters = guide?.chapters || [];

  const filteredChapters = useMemo(() => {
    if (!term) return chapters;
    return chapters.filter((chapter) => chapterMatchesSearch(chapter, term));
  }, [chapters, term]);

  const safeChapterIndex = filteredChapters.length
    ? Math.min(activeChapterIndex, filteredChapters.length - 1)
    : 0;
  const activeChapter = filteredChapters[safeChapterIndex];

  useEffect(() => {
    setActiveChapterIndex(0);
  }, [activeRole, term]);

  const selectRole = (roleId) => {
    setActiveRole(roleId);
  };

  return (
    <div className="user-guides-section ug-root">
      <div className="ug-roles" role="tablist" aria-label="Select user type">
        {USER_GUIDE_ROLES.map((role) => {
          const selected = activeRole === role.id;
          return (
            <button
              key={role.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`ug-role${selected ? ' is-on' : ''}`}
              onClick={() => selectRole(role.id)}
            >
              <span className="ug-role-mark" aria-hidden="true">
                {role.icon}
              </span>
              <span className="ug-role-copy">
                <span className="ug-role-name">{role.label}</span>
                <span className="ug-role-sub">{role.subtitle}</span>
              </span>
            </button>
          );
        })}
      </div>

      {chapters.length === 0 ? (
        <div className="ug-empty">Guides for this role will appear here.</div>
      ) : filteredChapters.length === 0 ? (
        <div className="ug-empty">No modules found. Try another search term.</div>
      ) : (
        <div className="ug-shell">
          <nav className="ug-nav" aria-label="Guide modules">
            <p className="ug-nav-heading">
              Modules
              <span className="ug-nav-count">{filteredChapters.length}</span>
            </p>
            <ul className="ug-module-list" role="list">
              {filteredChapters.map((chapter, index) => {
                const selected = safeChapterIndex === index;
                const panelId = `ug-module-panel-${activeRole}-${index}`;
                return (
                  <li key={`${chapter.title}-${chapter.number || index}`}>
                    <button
                      type="button"
                      id={`ug-module-tab-${activeRole}-${index}`}
                      className={`ug-module-btn${selected ? ' is-on' : ''}`}
                      aria-current={selected ? 'true' : undefined}
                      aria-controls={panelId}
                      onClick={() => setActiveChapterIndex(index)}
                    >
                      <span className="ug-module-label">{moduleListLabel(chapter)}</span>
                      {(chapter.sections || []).length > 1 ? (
                        <span className="ug-module-meta">{(chapter.sections || []).length} parts</span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div
            className="ug-panel ug-doc"
            id={`ug-module-panel-${activeRole}-${safeChapterIndex}`}
            role="region"
            aria-labelledby={`ug-module-tab-${activeRole}-${safeChapterIndex}`}
          >
            {activeChapter ? <ManualChapter chapter={activeChapter} /> : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserGuidesSection;
